/* eslint-disable @typescript-eslint/no-explicit-any */
import { EyeIcon, ArrowUp, ArrowDown, Trash2, Pencil, ExternalLink } from 'lucide-react'; // 🔹 tambah ikon ExternalLink
import { useCallback, useState } from 'react';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import TablePagination from './TablePagination';
import TableHeader from './TableHeader';
import TableLoadingState from './TableLoadingState';
import getNestedValue from '@/utils/functions/getNestedValue';
import TableEmptyState from './TableEmptyState';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import UpdateRoleModal from './UpdateRoleModal';

export interface PaginatedData {
  content: { [key: string]: unknown }[];
  totalElements: number;
  totalPages: number;
}

export interface MainTableColumn {
  name: string;
  label: string;
  className?: string;
  headerClassName?: string;
  sortable?: boolean;
  sortKey?: string;
  render?: (value: any, row: any, index: number) => React.ReactNode;
}

interface MainTableProps {
  title?: string;
  showHeader?: boolean;
  isLoading?: boolean;
  data: PaginatedData;
  pageConfig: DataConfig;
  setPageConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  columns: MainTableColumn[];
  showActions?: boolean;
  hideView?: boolean;
  redirectIdTaskOnly?: boolean;
  visibleColumns?: Set<string>;
  setVisibleColumns?: (columns: Set<string>) => void;
  variableCols?: MainTableColumn[];
  refetch?: () => void;
  handleExport?: () => void;
  isLoadingMutate?: boolean;
  extraAction?: React.ReactNode;
  handleDelete?: (row: any) => void;
  showDeleteAction?: boolean | ((row: any) => boolean);
  onDelete?: (row: any) => Promise<void> | void;
  showEditRoleAction?: boolean | ((row: any) => boolean);
  onUpdateRole?: (member: any, newRole: string) => void;
  showEditAction?: boolean | ((row: any) => boolean);
  onEdit?: (row: any) => void;
  showViewAction?: boolean | ((row: any) => boolean);
  onView?: (row: any) => void;
  useConfirmDelete?: boolean;
  showExternalLink?: boolean | ((row: any) => boolean);
  externalLinkUrl?: (row: any) => string;
  sectionClassName?: string;
  rowActions?: (row: any) => React.ReactNode;
}

export default function MainTable({
  title,
  showHeader = true,
  isLoading = false,
  data = { content: [], totalElements: 0, totalPages: 0 },
  pageConfig,
  setPageConfig,
  columns,
  showActions = true,
  hideView = false,
  redirectIdTaskOnly = false,
  visibleColumns,
  setVisibleColumns,
  variableCols,
  handleExport,
  isLoadingMutate,
  extraAction,
  onDelete,
  showDeleteAction = true,
  onUpdateRole,
  showEditRoleAction = true,
  showEditAction = true,
  onEdit,
  showViewAction = true,
  onView,
  useConfirmDelete = true,
  showExternalLink = false,
  externalLinkUrl,
  sectionClassName,
  rowActions,
}: Readonly<MainTableProps>) {
  const navigate = useNavigate();

  const handlePageChange = useCallback((newConfig: React.SetStateAction<DataConfig>) => {
    setPageConfig(newConfig);
  }, []);

  const handleSort = useCallback(
    (columnName: string) => {
      const isCurrentlySorted = pageConfig.orderBy === columnName;
      const newOrder = isCurrentlySorted ? (pageConfig.order === 'asc' ? 'desc' : 'asc') : 'asc';

      setPageConfig(prev => ({
        ...prev,
        orderBy: columnName,
        order: newOrder,
        page: 0,
      }));
    },
    [pageConfig.orderBy, pageConfig.order, setPageConfig],
  );

  const getSortIcon = (columnName: string) => {
    if (pageConfig.orderBy !== columnName) return null;
    return pageConfig.order === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const openDeleteModal = (row: any) => {
    setSelectedRow(row);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    const rowToDelete = selectedRow;
    setDeleteModalOpen(false);
    setSelectedRow(null);

    if (onDelete && rowToDelete) {
      await onDelete(rowToDelete);
    }
  };

  const [isUpdateRoleOpen, setIsUpdateRoleOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);

  const openUpdateRoleModal = (member: any) => {
    setSelectedMember(member);
    setIsUpdateRoleOpen(true);
  };

  const handleUpdateRole = (newRole: string) => {
    if (onUpdateRole && selectedMember) {
      onUpdateRole(selectedMember, newRole);
    }
  };

  return (
    <section className={classNames('bg-white dark:bg-gray-900 rounded pb-10 w-full grow', sectionClassName)}>
      {showHeader && (
        <TableHeader
          title={title ?? ''}
          hideView={hideView}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          variableCols={variableCols}
          handleExport={handleExport}
          isLoadingMutate={isLoadingMutate}
          extraAction={extraAction}
        />
      )}
      <div className="overflow-x-hidden pt-5">
        <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left table-auto border-collapse text-gray-500 dark:text-gray-300">
              <thead className="text-xs text-[#717680] dark:text-gray-200 bg-[#ECF0F7] dark:bg-gray-800">
                <tr>
                  <th scope="col" className="p-3 w-4 text-center">
                    No
                  </th>
                  {columns
                    .filter(col => !visibleColumns || visibleColumns.has(col.name))
                    .map((column, index) => (
                      <th
                        key={column.name + index}
                        scope="col"
                        className={`p-3 min-w-32 max-w-60 ${column.headerClassName ?? column.className ?? ''}`}
                      >
                        {column.sortable === false ? (
                          <span
                            className={classNames('flex items-center gap-1', {
                              'justify-center w-full': !!column.headerClassName,
                            })}
                          >
                            {column.label}
                          </span>
                        ) : (
                          <button
                            className={classNames('flex items-center gap-1 hover:text-gray-900 cursor-pointer', {
                              'font-medium text-primary hover:text-blue-700':
                                pageConfig.orderBy === (column.sortKey ?? column.name),
                              'justify-center w-full': !!column.headerClassName,
                            })}
                            onClick={() => handleSort(column.sortKey ?? column.name)}
                          >
                            {column.label}
                            {getSortIcon(column.sortKey ?? column.name)}
                          </button>
                        )}
                      </th>
                    ))}

                  {showActions && (
                    <th scope="col" className="p-3 text-center">
                      Aksi
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {isLoading && <TableLoadingState pageConfig={pageConfig} columns={columns} showActions={showActions} />}

                {!isLoading && !data?.content?.length && (
                  <TableEmptyState colSpan={columns.length + (showActions ? 2 : 1)} />
                )}

                {!isLoading &&
                  Array.isArray(data?.content) &&
                  data.content.filter(Boolean).map((request: any, index: number) => (
                    <tr className="bg-white dark:bg-gray-900 border-b dark:border-gray-700" key={request.id ?? index}>
                      <td className="p-3 text-center">{pageConfig.page * pageConfig.limit + index + 1}</td>
                      {columns
                        .filter(col => !visibleColumns || visibleColumns.has(col.name))
                        .map((column, indexCol) => (
                          <td key={column.name + indexCol} className="p-3 align-middle">
                            <div
                              className={classNames('overflow-hidden text-ellipsis', column.className)}
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {column.render
                                ? column.render(getNestedValue(request, column.name), request, index)
                                : getNestedValue(request, column.name) || '-'}
                            </div>
                          </td>
                        ))}
                      {showActions && (
                        <td className="p-3">
                          <div className="flex justify-center items-center gap-x-4">
                            {rowActions?.(request)}
                            {onUpdateRole && (
                              <button className="text-blue-600" onClick={() => openUpdateRoleModal(request)}>
                                Edit Role
                              </button>
                            )}

                            {onEdit &&
                              (typeof showEditAction === 'function' ? showEditAction(request) : showEditAction) && (
                                <button className="text-blue-600" onClick={() => onEdit(request)}>
                                  <Pencil size={16} />
                                </button>
                              )}

                            {onView &&
                              (typeof showViewAction === 'function' ? showViewAction(request) : showViewAction) && (
                                <button className="text-primary" onClick={() => onView(request)}>
                                  <EyeIcon size={16} />
                                </button>
                              )}

                            {showExternalLink &&
                              (typeof showExternalLink === 'function'
                                ? showExternalLink(request)
                                : showExternalLink) && (
                                <a
                                  href={externalLinkUrl ? externalLinkUrl(request) : '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-gray-400"
                                >
                                  <ExternalLink size={16} />
                                </a>
                              )}
                            {onDelete &&
                              (typeof showDeleteAction === 'function'
                                ? showDeleteAction(request)
                                : showDeleteAction) && (
                                <button
                                  className="text-red-500"
                                  aria-label="Hapus item"
                                  onClick={() => {
                                    if (useConfirmDelete) {
                                      openDeleteModal(request);
                                    } else if (onDelete) {
                                      onDelete(request);
                                    }
                                  }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={pageConfig.page}
            totalPages={data?.totalPages || 0}
            onPageChange={page => handlePageChange(prev => ({ ...prev, page }))}
            disabled={isLoading}
          />
        </div>
      </div>

      {useConfirmDelete && (
        <ConfirmDeleteModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedRow(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      <UpdateRoleModal
        open={isUpdateRoleOpen}
        onClose={() => setIsUpdateRoleOpen(false)}
        onSubmit={handleUpdateRole}
        currentRole={selectedMember?.role}
      />
    </section>
  );
}
